import { createSlice } from '@reduxjs/toolkit'
import { uniq } from 'lodash-es'
import { refreshChatDetails } from 'state/Account'
import API from 'state/API'
import { loadAuthDetails } from 'state/Auth'
import { refreshRestaurants } from 'state/Restaurants'

const initialState = {
	loading       : [],
	awaitingUpdate: [],
}

const slice = createSlice({
	name    : 'request',
	initialState,
	reducers: {
		loading: (state, { payload: { resource, status } }) => {
			if (status) {
				state.loading.push(resource)
			} else {
				state.loading = state.loading.filter(r => r != resource)
			}
		},
		requestUpdate: (state, { payload: slice }) => { state.awaitingUpdate.push(slice) },
		updateDone   : (state, { payload: slices }) => {
			state.awaitingUpdate = state.awaitingUpdate.filter(x => !slices.includes(x))
		},
	},
})

/**
 * Syntactic sugar to add a request-to-update during migrations.
 * Since the `request` state is not persisted, we can just recreate it
 * @param what {migrateWithRequestUpdate.SLICES|migrateWithRequestUpdate.SLICES[]}
 * @returns {function(*): *&{request: {awaitingUpdate: *|[*], loading: boolean}}}
 * @example ```js
 *  const migrations = {
 *    28: migrateWithRequestUpdate(UPDATABLE_SLICES.RESTAURANTS),
 *    29: state => {
 *      //other changes to state
 *      state = migrateWithRequestUpdate(UPDATABLE_SLICES.RESTAURANTS)(state)
 *      return state
 *    }
 *  }
 * ```
 */
export function migrateWithRequestUpdate(what) {
	return state => ({
		...state,
		request: {
			...initialState,
			awaitingUpdate: Array.isArray(what)? what : [what],
		},
	})
}

const UPDATABLE_SLICES = {
	AUTH       : 'auth',
	CHAT       : 'chat',
	RESTAURANTS: 'restaurant',
}

slice.actions.requestUpdate.SLICES = UPDATABLE_SLICES
migrateWithRequestUpdate.SLICES    = UPDATABLE_SLICES

//not exactly official, but enough for some organization
slice.thunks = {
	runPendingUpdates: async (dispatch, getState) => {
		const awaiting = uniq(getState().request.awaitingUpdate)
		if (awaiting.length) {
			const done = []

			for (let awaitingSlice of awaiting) {
				switch (awaitingSlice) {
					case UPDATABLE_SLICES.AUTH:
						dispatch(loadAuthDetails(null, true))
						break

					case UPDATABLE_SLICES.CHAT:
						dispatch(refreshChatDetails)
						break

					case UPDATABLE_SLICES.RESTAURANTS:
						dispatch(refreshRestaurants)
						break

					default:
						console.warn('Slice forced update not implemented yet', awaitingSlice)
						continue
				}
				done.push(awaitingSlice)
			}
			dispatch(slice.actions.updateDone(done))

			if (awaiting.length > done.length) {
				console.error({ missingSlices: getState().request.awaitingUpdate })
				throw Error('Cannot update all slices needed')
			}
		}
	},
}

/**
 * @param resource {string|string[]}
 * @returns {function({request: {loading: *}}): *}
 */
export const getLoading = resource => {
	return ({ request: { loading } }) =>
		resource == API.RESOURCES.FORCED
			|| (typeof resource == 'string' && loading.includes(resource))
			|| (Array.isArray(resource) && resource.some(r => loading.includes(r)))
}

export const { loading, requestUpdate } = slice.actions
export const { runPendingUpdates }      = slice.thunks
export default slice.reducer
